// Colour columns in the catalog store packed RGBA: (R<<24)|(G<<16)|(B<<8)|A  → hex 0xRRGGBBAA.
// Verified against the importer: modules/ucmaterialscsv/scripts/update_color_tones.php:94.
//  - 'int'    columns (uc_decors.color/edge_core_color, uc_decor_color_tones.color) hold the uint32.
//  - 'hexstr' columns (uc_material_diffuse.color_overlay/color_ambient) hold a "0xRRGGBBAA" string.
export type ColorKind = 'int' | 'hexstr';

const COLOR_FIELDS: Record<string, Record<string, ColorKind>> = {
    decor:            { color: 'int', edge_core_color: 'int' },
    color_tone:       { color: 'int' },
    material_diffuse: { color_overlay: 'hexstr', color_ambient: 'hexstr' },
};

export function colorKind(entity: string, field: string): ColorKind | null {
    return COLOR_FIELDS[entity]?.[field] ?? null;
}

export interface Rgba { r: number; g: number; b: number; a: number; }

/** Parse a stored colour value (uint32 or "0xRRGGBBAA") into RGBA bytes, or null. */
export function parseColor(kind: ColorKind, value: unknown): Rgba | null {
    if (value == null || value === '') return null;
    let u: number;
    if (kind === 'int') {
        u = Number(value);
    } else {
        u = parseInt(String(value).replace(/^0x/i, ''), 16);
    }
    if (!Number.isFinite(u)) return null;
    u = u >>> 0;
    return {
        r: (u >>> 24) & 0xff,
        g: (u >>> 16) & 0xff,
        b: (u >>> 8) & 0xff,
        a: u & 0xff,
    };
}

/** Pack RGBA back into the column's storage form. */
export function packColor(kind: ColorKind, c: Rgba): string {
    const u = (((c.r << 24) | (c.g << 16) | (c.b << 8) | c.a) >>> 0);
    if (kind === 'int') return String(u);
    return '0x' + u.toString(16).toUpperCase().padStart(8, '0');
}

export const cssRgb = (c: Rgba): string => `rgb(${c.r}, ${c.g}, ${c.b})`;
/** #RRGGBB for an <input type=color> (which ignores alpha). */
export const hex6 = (c: Rgba): string =>
    '#' + [c.r, c.g, c.b].map((n) => n.toString(16).padStart(2, '0')).join('').toUpperCase();
/** #RRGGBBAA label for display. */
export const hex8 = (c: Rgba): string =>
    hex6(c) + c.a.toString(16).padStart(2, '0').toUpperCase();

export function rgbFromHex6(hex: string): { r: number; g: number; b: number } {
    const h = hex.replace(/^#/, '');
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}
